import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useState } from "react";
import {
    ActivityIndicator,
    Alert,
    Appearance,
    Pressable,
    SafeAreaView,
    StyleSheet,
    Switch,
    Text,
    TextInput,
    useColorScheme,
    View,
} from "react-native";
import { supabase } from "../services/supabase";

const PIX_CHAVE_KEY = "pix_chave";
const PIX_TITULAR_KEY = "pix_titular";
const THEME_STORAGE_KEY = "app_theme";

export default function TelaConfiguracoes() {
  const [chavePix, setChavePix] = useState("");
  const [nomeTitular, setNomeTitular] = useState("");
  const [tema, setTema] = useState<"light" | "dark">("light");
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const colorScheme = useColorScheme();
  const modoEscuro = tema === "dark";

  useEffect(() => {
    async function carregarConfiguracoes() {
      try {
        const [chaveSalva, titularSalvo, temaSalvo] = await Promise.all([
          AsyncStorage.getItem(PIX_CHAVE_KEY),
          AsyncStorage.getItem(PIX_TITULAR_KEY),
          AsyncStorage.getItem(THEME_STORAGE_KEY),
        ]);

        setChavePix(chaveSalva ?? "");
        setNomeTitular(titularSalvo ?? "");

        const temaInicial =
          temaSalvo === "light" || temaSalvo === "dark"
            ? temaSalvo
            : colorScheme === "dark"
              ? "dark"
              : "light";

        setTema(temaInicial);
        Appearance.setColorScheme(temaInicial);
      } catch (error) {
        console.warn("Erro ao carregar configurações de Pix:", error);
        Alert.alert(
          "Erro",
          "Não foi possível carregar as configurações salvas.",
        );
      } finally {
        setCarregando(false);
      }
    }

    carregarConfiguracoes();
  }, [colorScheme]);

  async function salvarConfiguracoes() {
    try {
      setSalvando(true);
      await Promise.all([
        AsyncStorage.setItem(PIX_CHAVE_KEY, chavePix.trim()),
        AsyncStorage.setItem(PIX_TITULAR_KEY, nomeTitular.trim()),
      ]);

      Alert.alert("Sucesso", "Configuração de Pix salva com sucesso.");
    } catch (error) {
      console.warn("Erro ao salvar configurações de Pix:", error);
      Alert.alert("Erro", "Não foi possível salvar a chave Pix.");
    } finally {
      setSalvando(false);
    }
  }

  async function alternarTema(novoValor: boolean) {
    const proximoTema: "light" | "dark" = novoValor ? "dark" : "light";

    try {
      setTema(proximoTema);
      await AsyncStorage.setItem(THEME_STORAGE_KEY, proximoTema);
      Appearance.setColorScheme(proximoTema);
    } catch (error) {
      console.warn("Erro ao salvar tema:", error);
      Alert.alert("Erro", "Não foi possível salvar a preferência de tema.");
    }
  }

  async function handleLogout() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        Alert.alert("Erro", error.message);
      }
    } catch (err) {
      Alert.alert("Erro", "Erro desconhecido ao tentar sair.");
    }
  }

  return (
    <SafeAreaView
      style={[styles.container, modoEscuro && styles.containerDark]}
    >
      <View style={styles.content}>
        <View style={[styles.card, modoEscuro && styles.cardDark]}>
          <Text style={[styles.title, modoEscuro && styles.titleDark]}>
            Chave Pix
          </Text>
          <Text style={[styles.subtitle, modoEscuro && styles.subtitleDark]}>
            Salve sua chave Pix para incluir automaticamente nas mensagens de
            cobrança.
          </Text>

          {carregando ? (
            <View style={styles.loadingBox}>
              <ActivityIndicator
                size="small"
                color={modoEscuro ? "#93c5fd" : "#2563eb"}
              />
              <Text style={[styles.loadingText, modoEscuro && styles.textDark]}>
                Carregando...
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.fieldGroup}>
                <Text style={[styles.label, modoEscuro && styles.textDark]}>
                  Chave Pix
                </Text>
                <TextInput
                  value={chavePix}
                  onChangeText={setChavePix}
                  placeholder="ex.: 12345678900"
                  style={[styles.input, modoEscuro && styles.inputDark]}
                  placeholderTextColor={modoEscuro ? "#9ca3af" : "#6b7280"}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              <View style={styles.fieldGroup}>
                <Text style={[styles.label, modoEscuro && styles.textDark]}>
                  Nome do titular
                </Text>
                <TextInput
                  value={nomeTitular}
                  onChangeText={setNomeTitular}
                  placeholder="Seu nome completo"
                  style={[styles.input, modoEscuro && styles.inputDark]}
                  placeholderTextColor={modoEscuro ? "#9ca3af" : "#6b7280"}
                  autoCapitalize="words"
                />
              </View>

              <Pressable
                style={[styles.button, salvando && styles.buttonDisabled]}
                onPress={salvarConfiguracoes}
                disabled={salvando}
              >
                {salvando ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.buttonText}>Salvar configuração</Text>
                )}
              </Pressable>
            </>
          )}
        </View>

        <View
          style={[styles.card, modoEscuro && styles.cardDark, styles.cardTheme]}
        >
          <Text style={[styles.title, modoEscuro && styles.titleDark]}>
            Aparência
          </Text>

          <View style={styles.themeRow}>
            <Text style={[styles.themeText, modoEscuro && styles.textDark]}>
              {modoEscuro ? "Tema Escuro" : "Tema Claro"}
            </Text>
            <Switch
              value={modoEscuro}
              onValueChange={alternarTema}
              trackColor={{ false: "#d1d5db", true: "#93c5fd" }}
              thumbColor={modoEscuro ? "#2563eb" : "#f4f4f5"}
            />
          </View>
        </View>

        <View
          style={[styles.card, modoEscuro && styles.cardDark, styles.cardTheme]}
        >
          <Text style={[styles.title, { color: "#dc2626" }]}>
            Sessão
          </Text>
          <Text style={[styles.subtitle, modoEscuro && styles.subtitleDark, { marginBottom: 12 }]}>
            Sair do aplicativo e desconectar a conta deste dispositivo.
          </Text>

          <Pressable
            style={styles.buttonLogout}
            onPress={handleLogout}
          >
            <Text style={styles.buttonLogoutText}>Sair da Conta</Text>
          </Pressable>
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  containerDark: {
    backgroundColor: "#0f172a",
  },
  content: {
    flex: 1,
    justifyContent: "center",
    padding: 20,
    gap: 16,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  cardDark: {
    backgroundColor: "#111827",
    borderColor: "#374151",
  },
  cardTheme: {
    paddingVertical: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: "800",
    color: "#111827",
    marginBottom: 6,
  },
  titleDark: {
    color: "#f9fafb",
  },
  subtitle: {
    fontSize: 14,
    color: "#6b7280",
    marginBottom: 20,
    lineHeight: 20,
  },
  subtitleDark: {
    color: "#d1d5db",
  },
  fieldGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 13,
    fontWeight: "700",
    color: "#374151",
    marginBottom: 8,
  },
  textDark: {
    color: "#e5e7eb",
  },
  input: {
    backgroundColor: "#f9fafb",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: "#111827",
  },
  inputDark: {
    backgroundColor: "#1f2937",
    borderColor: "#374151",
    color: "#f9fafb",
  },
  button: {
    backgroundColor: "#2563eb",
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 8,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  buttonText: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "700",
  },
  buttonLogout: {
    backgroundColor: "#dc2626",
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonLogoutText: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "700",
  },
  loadingBox: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
    gap: 10,
  },
  loadingText: {
    color: "#4b5563",
    fontSize: 14,
  },
  themeRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
  },
  themeText: {
    fontSize: 15,
    color: "#111827",
    fontWeight: "600",
  },
});
